
import {Component, Vue, Watch} from "vue-property-decorator";
import ListRequest, {Filter, Ordering, OrderType} from "@/models/ListRequest";
import {mapState} from "vuex";
import {TRANSLATIONS_FETCH} from "@/store/ActionTypes";
import vBreadcrumbs from "@/components/vBreadcrumbs.vue";
import vNutritionLabel from "@/components/vNutritionLabel.vue";
import RecipesRepository from "@/utils/API/RecipesRepository";
import AllergenRepository from "@/utils/API/AllergenRepository";
import {Recipe} from "@/models/Recipes/Recipe";
import {Nutritions} from "@/models/Recipes/Nutritions";
import vPreLoader from "@/components/vPreLoader.vue";
import DishesRepository from "@/utils/API/DishesRepository";
import {FilterRecipesNames} from "@/models/Recipes/FilterRecipesNames";
import vSelect from "@/components/vSelect.vue";
import vSelectFx from "@/components/vSelectFx.vue";
import {getBoldString, mapNutritions, debounce, getPreviewNutrientsForCountry} from "@/utils/Helpers";

import vModal from "@/components/vModal.vue";
import {RecipeComposition} from "@/models/Recipes/RecipeComposition";
import {RecipeServingType} from "@/models/Recipes/RecipeServingType";
import {RecipeCalculate, RecipeCalculateSubrecipe} from "@/models/Recipes/RecipeCalculate";
import {Subrecipe} from "@/models/Recipes/Subrecipe";
import {RecipeCompositionMeasurement} from "@/models/Recipes/RecipeCompositionMeasurement";
import {ServingSizeMeasurement} from "@/models/Measurement/ServingSizeMeasurement";
import {LabelTypeMetadata} from "@/models/NutritionLabels/LabelTypeMetadata";
import VCalculationMethodModal from "@/components/vCalculationMethodModal.vue";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import {CalculationMethodModalData} from "@/models/Recipes/CalculationMethodModalData";
import {Dish} from "@/models/Dishes/Dish";
import {AccountState} from "@/store/modules/AccountModule";
import {CaloriesCalculationMethod} from "@/models/Recipes/CaloriesCalculationMethod";
import {DailyValueCalculationMethod} from "@/models/Recipes/DailyValueCalculationMethod";
import {PrintRecipeDetails} from "@/models/Recipes/PrintRecipeDetails";
import VRecipePrintout from "@/components/vRecipePrintout.vue";
import NutritionLabelRepository from "@/utils/API/NutritionLabelRepository";
import { NutritionCountry } from "@/models/NutritionLabels/NutritionCountry";
import VUnroundedValuesWarning from "@/components/vUnroundedValuesWarning.vue";
import {createDefaultProductType, RecipeProductType} from "@/models/Recipes/RecipeProductType";
import {PromiseReject, PromiseResolve} from "@/typings/common";
import {Snotify} from "vue-snotify";
import { RecipeMetadata } from "@/models/Recipes/RecipeMetadata";
import vIconWithTooltip from "@/components/vIconWithTooltip.vue";
import VFoPWarning from "@/components/vFoPWarning.vue";
import VClaimsAndStatementsModal from "@/components/vClaimsAndStatementsModal.vue";
import VEnabledClaimsList from "@/components/vEnabledClaimsList.vue";
import ClaimsRepository from "@/utils/API/ClaimsRepository";
import VPreviewLabel from "@/components/vPreviewLabel.vue";
import {RoundingRules} from "@/models/NutritionLabels/RoundingRules";
import VBanner from "@/components/vBanner.vue";
import {CreateRecipe} from "@/models/Recipes/CreateRecipe";
import {UpdateRecipe} from "@/models/Recipes/UpdateRecipe";
import {ErrorResponse} from "@/utils/API/ResultTypes/ErrorResponse";
import { CanadaFOPHighInMetadata } from "@/models/NutritionLabels/CanadaFOPHighInMetadata";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import {AddOnType} from "@/models/Subscriptions/AddOns/AddOnType";
import AllergensConfiguration from "@/components/labelConfiguration/AllergensConfiguration.vue";
import VAllergenStatusIcon from "@/components/allergens/VAllergenStatusIcon.vue";
import { getAllergenStatusMeta } from "@/utils/allergenStatus";
import Toggle from "@/components/bootstrap/Toggle.vue";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {SavedAllergen} from "@/models/Allergen/SelectedAllergen";
import {AllergenConfigurationTargetType} from "@/models/Allergen/AllergenConfigurationData";
import {MlAnalysisResponse} from "@/models/AllergenML/AllergenML";
import AllergenMLRepository from "@/utils/API/AllergenMLRepository";

const vExportLabel = () => import("@/components/vExportLabel.vue");

interface DishesManagementState {
  count: number;
  totalCount: number;
  page: number;
  pageSize: number;
  filters: Filter[];
  orders: Ordering;
  loading: boolean;
  recipes: Recipe[];

  dish: Dish
}

@Component({
  components: {
    VUpsellModal,
    VBanner,
    VAllergenStatusIcon,
    VPreviewLabel,
    VFoPWarning,
    VUnroundedValuesWarning,
    VRecipePrintout,
    VCalculationMethodModal,
    vBreadcrumbs,
    vNutritionLabel,
    vModal,
    vPreLoader,
    vExportLabel,
    vSelect,
    vSelectFx,
    vIconWithTooltip,
    VClaimsAndStatementsModal,
    VEnabledClaimsList,
    Toggle,
    AllergensConfiguration
  },
  methods: {
    debounce
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class DishesManagement extends Vue {
  localizationState: LocalizationState;
  accountState: AccountState;

  DailyValueCalculationMethod = DailyValueCalculationMethod;

  calculationMethodModal: CalculationMethodModalData = new CalculationMethodModalData();
  roundingRuleIndex: number = 0;

  selectedRoundingRule: RoundingRules = { id: 0, name: 'FDA' };
  nutritionRules = [
    { id: 0, name: 'FDA' },
    { id: 1, name: 'FSIS (2025)' },
    { id: 2, name: 'FSIS (2007)' },
  ];

  recipeMetadata: RecipeMetadata = {
    recipeId: 0,
    countryId: 0,
    referenceAmount: 0,
    productTypeId: 0,
    roundingRuleIndex: 0,
    isSupplementLabel: false,
    showAllergenOnPrintout: false,
  };

  dishesManagementState: DishesManagementState = {
    count: 0,
    totalCount: 0,
    page: 1,
    pageSize: 5,
    filters: [],
    orders: { field: "Name", orderType: OrderType.Asc },
    loading: false,
    recipes: [],
    dish: {
      id: 0,
      name: "",
      description: "",
      instructions: null,
      servings: 1,
      recipeCategory: null,
      ingredients: [],
      nutritionAnalysis: new Nutritions(
        [],
        'g',
        0,
        0,
        0,
        0,
        0,
        null,
        null,
        null,
        null
      ),
      childRecipes: [],
      caloriesCalculationMethod: CaloriesCalculationMethod.Summation,
      dailyValueCalculationMethod: DailyValueCalculationMethod.Rounded,
      proteinDvCalculationMethod: 0,
      pdcaasScore: 1,
      nutritionCountry: null,
      referenceAmount: 0,
      recipeProductType: {
        id: 0,
        name: "",
        productType: 0,
        countryId: 0,
        createdOn: null
      },
      productTypeId: 0
    }
  };

  showDeleteDishModal = false;
  showExportLabelModal = false;
  showExportLabelDemoModal = false;
  isExportLabelDemoLoading = false;
  isDirty: boolean = true; // Tracks the saved state of the dish.

  enabledClaims: string[] = [];

  // General reference variables
  subrecipeServingSizes: RecipeCompositionMeasurement[] = [];
  subrecipeServingMeasurements: ServingSizeMeasurement[] = [];
  foodProductTypes: RecipeProductType[] = [];
  nutritionCountries: NutritionCountry[] = [];
  LabelType = LabelTypeMetadata;
  showClaimModal = false;
  isDisplayFop: boolean = true;
  isSupplementLabelLoaded: boolean = false;
  isRefreshingDishAllergenCard = false;
  selectedAllergens: SavedAllergen[] = [];
  allergenContainsText: string = "";
  mayContainText: Partial<Record<LabelLanguage, string[]>> = {};
  dishAllergenLanguage: keyof typeof LabelLanguage = "English";
  showIncludeAllergenConfigurationModal = false;
  includeAllergenConfigurationModalLead = "";
  includeAllergenConfigurationModalMessage = "";
  private resolveIncludeAllergenConfigurationModal: ((value: boolean) => void) | null = null;

  printRecipeMeta: PrintRecipeDetails = {
    ingredients: [],
    brandingLogo: null,
    statementsAndClaims: []
  };

  showTrialUpsellModal: boolean = false;
  defaultTrialUpsellMessage = "This feature requires a paid subscription to use.";
  trialUpsellMessage = this.defaultTrialUpsellMessage;
  exportLabelDemoSrc = "https://app.supademo.com/embed/cmn3doqd369pqsjdoewdkiato?embed_v=2&utm_source=embed";

  provide() {
    return {
      bopFetchPreview: () => {},
    };
  }

  onAllergenValueUpdated(value: SavedAllergen[]) {
    this.selectedAllergens = [...value];
  }

  openIncludeAllergenConfigurationModal(lead: string, message: string) {
    this.includeAllergenConfigurationModalLead = lead;
    this.includeAllergenConfigurationModalMessage = message;
    this.showIncludeAllergenConfigurationModal = true;

    return new Promise<boolean>((resolve) => {
      this.resolveIncludeAllergenConfigurationModal = resolve;
    });
  }

  confirmIncludeAllergenConfigurationModal() {
    this.showIncludeAllergenConfigurationModal = false;
    const resolve = this.resolveIncludeAllergenConfigurationModal;
    this.resolveIncludeAllergenConfigurationModal = null;
    resolve?.(true);
  }

  cancelIncludeAllergenConfigurationModal() {
    this.showIncludeAllergenConfigurationModal = false;
    const resolve = this.resolveIncludeAllergenConfigurationModal;
    this.resolveIncludeAllergenConfigurationModal = null;
    resolve?.(false);
  }

  async beforeOpenDishAiDetector() {
    return true;
  }

  async onDishAiAnalysisApplied(result: MlAnalysisResponse) {
    const dish = this.dishesManagementState?.dish;

    if (!dish) {
      return;
    }

    this.applyDishAnalysisStatuses(result);

    await this.refreshDishAllergenStatuses(dish.id);
  }

  onMayContainTextUpdated(value: Partial<Record<LabelLanguage, string[]>>) {
    this.mayContainText = { ...value };
  }

  cloneMayContainText(value: Partial<Record<LabelLanguage, string[]>>) {
    const cloned: Partial<Record<LabelLanguage, string[]>> = {};

    Object.keys(value || {}).forEach((key) => {
      const language = key as LabelLanguage;
      cloned[language] = [...(value[language] || [])];
    });

    return cloned;
  }

  mergeMayContainText(
    currentValue: Partial<Record<LabelLanguage, string[]>>,
    incomingValue: Partial<Record<LabelLanguage, string[]>>
  ) {
    const merged = this.cloneMayContainText(currentValue || {});

    Object.keys(incomingValue || {}).forEach((key) => {
      const language = key as LabelLanguage;
      const existingValues = new Set((merged[language] || []).map((value) => (value || "").trim().toLowerCase()));

      (incomingValue[language] || []).forEach((value) => {
        const trimmedValue = (value || "").trim();

        if (!trimmedValue) {
          return;
        }

        if (!existingValues.has(trimmedValue.toLowerCase())) {
          merged[language] = [...(merged[language] || []), trimmedValue];
          existingValues.add(trimmedValue.toLowerCase());
        }
      });
    });

    return merged;
  }

  mergeRecipeAllergenConfigurationIntoDish(configuration: {
    allergens?: SavedAllergen[];
    mayContainText?: Partial<Record<LabelLanguage, string[]>>;
  }) {
    const nextAllergensById = new Map<number, SavedAllergen>();

    (this.selectedAllergens || []).forEach((allergen: SavedAllergen) => {
      if (typeof allergen === "object" && allergen?.id) {
        nextAllergensById.set(allergen.id, allergen);
      }
    });

    (configuration.allergens || []).forEach((allergen: SavedAllergen) => {
      if (typeof allergen === "object" && allergen?.id && !nextAllergensById.has(allergen.id)) {
        nextAllergensById.set(allergen.id, allergen);
      }
    });

    this.selectedAllergens = Array.from(nextAllergensById.values());
    this.mayContainText = this.mergeMayContainText(
      this.mayContainText || {},
      configuration.mayContainText || {}
    );
  }

  analysisResultHasDetectedAllergen(result: MlAnalysisResponse) {
    if ((result.ingredients || []).length > 0) {
      return result.ingredients.some((ingredientResult) =>
        this.analysisEntryHasDetectedAllergen(ingredientResult.allergens, ingredientResult.overrideMarkers || [])
      );
    }

    return this.analysisEntryHasDetectedAllergen(result.allergens || [], []);
  }

  analysisEntryHasDetectedAllergen(
    allergens: Array<{ id: string }> = [],
    overrideMarkers: Array<{ allergenCode: string; isAllergen: boolean }> = []
  ) {
    const detectedCodes = new Set(
      allergens
        .map((allergen) => (allergen.id || "").trim().toLowerCase())
        .filter(Boolean)
    );

    for (const marker of overrideMarkers) {
      const code = (marker.allergenCode || "").trim().toLowerCase();

      if (!code) {
        continue;
      }

      if (marker.isAllergen) {
        detectedCodes.add(code);
      }
      else {
        detectedCodes.delete(code);
      }
    }

    return detectedCodes.size > 0;
  }

  applyDishAnalysisStatuses(result: MlAnalysisResponse) {
    const dish = this.dishesManagementState?.dish;

    if (!dish) {
      return;
    }

    dish.hasAllergenAnalysis = true;
    dish.hasAllergen = this.analysisResultHasDetectedAllergen(result);

    const childRecipeAnalysisById = new Map<number, boolean>();
    const childRecipeAnalysisByName = new Map<string, boolean>();

    for (const ingredientResult of result.ingredients || []) {
      const subrecipeId = this.extractSubrecipeIdFromLineage(ingredientResult.lineagePath);
      const subrecipeName = this.extractSubrecipeNameFromLineage(ingredientResult.lineagePath);

      if (!subrecipeId) {
        if (!subrecipeName) {
          continue;
        }
      }

      const hasDetectedAllergen = this.analysisEntryHasDetectedAllergen(
        ingredientResult.allergens,
        ingredientResult.overrideMarkers || []
      );

      if (subrecipeId) {
        if (!childRecipeAnalysisById.has(subrecipeId)) {
          childRecipeAnalysisById.set(subrecipeId, false);
        }

        childRecipeAnalysisById.set(subrecipeId, childRecipeAnalysisById.get(subrecipeId) || hasDetectedAllergen);
      }

      if (subrecipeName) {
        if (!childRecipeAnalysisByName.has(subrecipeName)) {
          childRecipeAnalysisByName.set(subrecipeName, false);
        }

        childRecipeAnalysisByName.set(
          subrecipeName,
          childRecipeAnalysisByName.get(subrecipeName) || hasDetectedAllergen
        );
      }
    }

    for (const childRecipe of dish.childRecipes || []) {
      const childRecipeId = childRecipe?.recipe?.id;
      const childRecipeName = this.normalizeLineageSegment(childRecipe?.recipe?.name);
      const hasMatchedStatus = Boolean(
        (childRecipeId && childRecipeAnalysisById.has(childRecipeId))
        || (childRecipeName && childRecipeAnalysisByName.has(childRecipeName))
      );

      if (!hasMatchedStatus) {
        continue;
      }

      childRecipe.recipe.hasAllergenAnalysis = true;
      childRecipe.recipe.hasAllergen = Boolean(
        (childRecipeId ? childRecipeAnalysisById.get(childRecipeId) : false)
        || (childRecipeName ? childRecipeAnalysisByName.get(childRecipeName) : false)
      );
    }
  }

  extractSubrecipeIdFromLineage(lineagePath: string | null | undefined) {
    const match = (lineagePath || "").match(/Subrecipe\/(\d+)/i);
    return match ? Number(match[1]) : 0;
  }

  extractSubrecipeNameFromLineage(lineagePath: string | null | undefined) {
    const matches = [...(lineagePath || "").matchAll(/Subrecipe\/([^>]+)/gi)];

    if (matches.length === 0) {
      return "";
    }

    return this.normalizeLineageSegment(matches[matches.length - 1]?.[1]);
  }

  normalizeLineageSegment(value: string | null | undefined) {
    return (value || "").trim().toLowerCase();
  }

  async refreshDishAllergenStatuses(dishId: number) {
    if (!dishId) {
      return;
    }

    this.isRefreshingDishAllergenCard = true;

    try {
      const response = await RecipesRepository.getRecipeDetails(dishId, true);

      if (!response?.data) {
        return;
      }

      await this.mapCombinedRecipeDetails(response.data);
      await this.loadAllergenConfiguration(response.data.id);
      await this.hydrateDishStatusesFromSavedAnalysis(response.data.id);
    }
    catch {
      return;
    }
    finally {
      this.isRefreshingDishAllergenCard = false;
    }
  }

  async hydrateDishStatusesFromSavedAnalysis(dishId: number) {
    const restaurantId = this.accountState?.userDetails?.restaurantId;
    const countryId = this.countryId;

    if (!this.isAllergenAiAddonActive || !dishId || !restaurantId || !countryId) {
      return;
    }

    try {
      const response = await AllergenMLRepository.getSavedRecipeAnalysis({
        restaurantId,
        recipeId: dishId,
        isCombinedRecipe: true,
        countryId,
        nutrientSource: countryId,
      });

      this.applyDishAnalysisStatuses(response.data);
    }
    catch {
      return;
    }
  }

  async saveAllergenConfiguration(recipeId: number) {
    await AllergenRepository.saveAllergenConfiguration({
      targetType: AllergenConfigurationTargetType.Recipe,
      targetId: recipeId,
      allergens: this.selectedAllergens || [],
      mayContainText: this.mayContainText || {}
    });
  }

  async saveCurrentAllergenConfiguration() {
    const recipeId = this.dishesManagementState?.dish?.id;

    if (!recipeId) {
      return;
    }

    await this.saveAllergenConfiguration(recipeId);
  }

  async loadAllergenConfiguration(recipeId: number) {
      try {
        const response = await AllergenRepository.getStoredAllergenConfiguration(
          AllergenConfigurationTargetType.Recipe,
          recipeId
        );

      this.selectedAllergens = response.data?.allergens || [];
      this.mayContainText = response.data?.mayContainText || {};
    }
    catch {
      this.selectedAllergens = [];
      this.mayContainText = {};
    }
  }

  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.defaultTrialUpsellMessage;
  }

  /**
   * Determines the default label type based on the recipe metadata properties.
   * Evaluates conditions such as whether the label corresponds to supplement facts,
   * FSIS requirements, or standard nutrition facts.
   *
   * @return {LabelTypeMetadata} The default label type, which can be SupplementFacts, FSIS, or NutritionFacts.
   */
  get defaultLabelType() {
    if (this.recipeMetadata.isSupplementLabel) {
      return LabelTypeMetadata.SupplementFacts;
    }

    if (this.recipeMetadata.roundingRuleIndex != 0 && this.recipeMetadata.countryId == NutritionLabelCountry.US) {
      return LabelTypeMetadata.FSIS;
    }

    return LabelTypeMetadata.NutritionFacts;
  }

  /**
   * Retrieves the name of the country associated with the nutrition data.
   *
   * @return {string | null} The name of the country if available, or null if no country information exists.
   */
  get countryName(): string | null {
    if (this.dishesManagementState.dish.nutritionCountry) {
      return this.dishesManagementState.dish.nutritionCountry.name;
    }

    return null;
  }

  /**
   * Retrieves the id of the country associated with the nutrition data.
   *
   * @return {number | null} The id of the country if available, or null if no country information exists.
   */
  get countryId(): number | null {
    if (this.dishesManagementState.dish.nutritionCountry) {
      return this.dishesManagementState.dish.nutritionCountry.id;
    }

    return null;
  }

  get containsAllergensText() {
    if (!this.recipeMetadata.showAllergenOnPrintout) {
      return "";
    }

    return this.allergenContainsText;
  }

  get isEmptyRecipe(){
    return this.dishesManagementState.dish.childRecipes.length == 0;
  }

  get highInMetadata(){
    if(this.dishesManagementState.dish.nutritionAnalysis.additionalLabelInformation){
      return this.dishesManagementState.dish.nutritionAnalysis.additionalLabelInformation[this.LabelType.FrontOfPack].highInMetadata;
    }
    else{
      CanadaFOPHighInMetadata.None;
    }
  }

  get exportLabelTitle(): string {
    const title = this.translations.get('rec_export_label') ?? '';
    const recipeName = this.dishesManagementState?.dish?.name ?? '';

    return `${title} (${recipeName})`;
  }

  // Watches
  @Watch("caloriesCalculationMethod")
  onCaloriesCalculationMethodChange() {
    this.calculateNutritions();
  }

  get caloriesCalculationMethod() {
    return this.calculationMethodModal.selectedCalculationMethod;
  }

  tryInitialSave() {
    if (!this.dishesManagementState.dish.id && this.dishesManagementState.dish.name) {
      this.submit();
    }
  }

  initSearch() {
    let selector = <any>$("#searchRecipes");
    if (selector.hasClass("select2-hidden-accessible")) {
      selector.html("").select2();
    }
    let searchTerm = "";
    selector
      .select2({
        allowClear: false,
        placeholder: "Search Recipes",
        minimumInputLength: 3,
        ajax: {
          delay: 500,
          transport: (params: any, success: any) => {
            RecipesRepository.filterRecipesNames(
              params.data.term ? params.data.term : ""
            ).then((response) => {
              success(response);
            });
            searchTerm = params.data.term;
          },
          processResults: (response: any) => {
            let data: any[] = [];

            response.data.result.map((c: FilterRecipesNames) => {
              let children: any = [];

              if (c.recipes && c.recipes.length) {
                data.push({ id: `cat-${c.id}`, text: getBoldString(c.name, "") });

                c.recipes.map((recipe: Recipe) => {
                  children.push({ id: recipe.id, text: getBoldString(recipe.name, searchTerm) });
                });
              }

              data.push({ children: children });
            });

            return { results: data };
          },
        },
        escapeMarkup: function(element:string){
          return element;
        },
      })
      .on("select2:select", (eventData: any) => {
        let value: string = <string>eventData.target.value;
        if (value.startsWith("cat")) {
          let parts = value.split("-");
          let categoryId = parts[1];

          this.dishesManagementState.filters = [
            {
              name: "RecipeCategoryId",
              value: categoryId,
            },
          ];
        }
        else {
          this.dishesManagementState.filters = [{ name: "Id", value: value }];
          this.dishesManagementState.page = 1;
        }
        this.requestRecipes(
          new ListRequest({
            page: this.dishesManagementState.page,
            pageSize: this.dishesManagementState.pageSize,
            filters: this.dishesManagementState.filters,
            orders: [this.dishesManagementState.orders],
          })
        );
      });
  }

  setDirtyState() {
    this.isDirty = true;
  }

  clearDirtyState() {
    this.isDirty = false;
  }

  async fetchPackageType(countryId: number = 0){
    await RecipesRepository.getProductTypesByCountry(countryId).then((response) => {
      if(response){
        this.foodProductTypes = [
          ...response.data,
        ];

        let productType = response.data.find(
          (x: RecipeProductType) => x.id == this.dishesManagementState.dish.productTypeId
        );

        if(productType) {
          this.dishesManagementState.dish.recipeProductType = productType;
        }
      }
      else{
        this.foodProductTypes = [];
        this.dishesManagementState.dish.recipeProductType = createDefaultProductType();
      }
    });
  }

  selectProductType(productType: RecipeProductType) {
    this.dishesManagementState.dish.recipeProductType = productType;
    this.calculateNutritions();
    this.setDirtyState();
  }

  openClaimsModal(){
    this.showClaimModal = true;
  }

  async showExportLabel() {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
      return;
    }

    if (!this.dishesManagementState.dish.id || this.isDirty) {
      return;
    }

    await this.updatePrintMeta();
    this.showExportLabelModal = true;
  }

  async updatePrintMeta() {
    const response = await RecipesRepository.getPrintRecipeDetails(this.dishesManagementState.dish.id);
    this.printRecipeMeta = response.data;
  }

  async printRecipe() {
    if (this.accountState.userDetails.isTrial) {
      this.showTrialUpsellModal = true;
      return;
    }

    if (!this.dishesManagementState.dish.id || this.isDirty) {
      return;
    }

    await this.updatePrintMeta();
    await new Promise((res) => setTimeout(res, 500));
    window.print();
  }

  editRecipe(id: number) {
    let routeData = this.$router.resolve({
      name: "recipesEdit",
      params: { id: `${id}` },
    });

    window.open(routeData.href, "_blank");
  }

  async addRecipeToDish(recipeId: number) {
    const recipe = this.dishesManagementState.recipes.find((r) => r.id === recipeId);

    if (!recipe) {
      return;
    }

    let recipeAllergenConfiguration = null;

    try {
      const response = await AllergenRepository.getStoredAllergenConfiguration(
        AllergenConfigurationTargetType.Recipe,
        recipeId
      );
      recipeAllergenConfiguration = response?.data || null;
    }
    catch {
      recipeAllergenConfiguration = null;
    }

    const subrecipeRecipe: Recipe = {
      ...recipe,
      hasAllergenAnalysis: recipe.hasAllergenAnalysis ?? false,
      hasAllergen: recipe.hasAllergen ?? false,
    };

    let subrecipe: Subrecipe = {
      id: recipe.id,
      recipe: subrecipeRecipe,
      isAddedSugar: false,
      quantity: 1,
      recipeCompositionMeasurementId: 1,
      hasVolumeUnitOfMeasurement: recipe.volume !== null,
      weightPerServing: recipe.weightPerServing,
      foodGroupClassificationId: null,
    };

    this.dishesManagementState.dish.childRecipes.push(subrecipe);

    const hasRecipeAllergenConfiguration = Boolean(
      recipeAllergenConfiguration
      && (
        (recipeAllergenConfiguration.allergens || []).length > 0
        || Object.values(recipeAllergenConfiguration.mayContainText || {}).some(
          (items) => ((items as string[] | undefined) || []).length > 0
        )
      )
    );

    if (hasRecipeAllergenConfiguration) {
      const shouldApplyRecipeAllergenConfiguration = await this.openIncludeAllergenConfigurationModal(
        "This recipe has saved allergen and may contain information.",
        "Do you want to include this information in this combined recipe?"
      );

      if (shouldApplyRecipeAllergenConfiguration) {
        this.mergeRecipeAllergenConfigurationIntoDish(recipeAllergenConfiguration);
      }
    }

    if(this.dishesManagementState.dish.id){
      this.calculateNutritions();
    }

    this.setDirtyState();
  }

  normalizeQuantity(quantity: number | string | null | undefined, fallback: number = 0): number {
    const parsedQuantity = Number(quantity);

    return Number.isFinite(parsedQuantity) ? parsedQuantity : fallback;
  }

  hasValidQuantity(quantity: number | string | null | undefined): boolean {
    if (quantity === "" || quantity === null || quantity === undefined) {
      return false;
    }

    return Number.isFinite(Number(quantity));
  }

  hasInvalidChildRecipeQuantity(): boolean {
    return this.dishesManagementState.dish.childRecipes.some(
      (subrecipe: Subrecipe) => !this.hasValidQuantity(subrecipe.quantity)
    );
  }

  onRecipeQuantityAdjust(subrecipe?: Subrecipe) {
    if (subrecipe && this.hasValidQuantity(subrecipe.quantity)) {
      subrecipe.quantity = this.normalizeQuantity(subrecipe.quantity);
    }

    this.setDirtyState();

    if (this.hasInvalidChildRecipeQuantity()) {
      return;
    }

    this.calculateNutritions();
  }

  onRecipeMeasureSelect(event: { id: number, name: string }, subrecipe: Subrecipe) {
    this.selectSubRecipeMeasurement(event, subrecipe);
    this.setDirtyState();
    this.calculateNutritions();
  }

  async updateRecipeCountryMetadata(selectedCountry: NutritionCountry, isSupplementLabel: boolean = false) {
    this.dishesManagementState.dish.nutritionCountry = { ...selectedCountry };
    this.recipeMetadata.isSupplementLabel = isSupplementLabel;

    let dishMetadata: RecipeMetadata = {
      recipeId: this.dishesManagementState.dish.id,
      countryId: this.countryId,
      referenceAmount: this.dishesManagementState.dish.referenceAmount,
      productTypeId: this.dishesManagementState.dish.productTypeId,
      roundingRuleIndex: 0,
      isSupplementLabel: isSupplementLabel,
      showAllergenOnPrintout: this.recipeMetadata.showAllergenOnPrintout
    };

    const response = await RecipesRepository.updateRecipeCountry(dishMetadata);

    this.dishesManagementState.dish.nutritionAnalysis = mapNutritions(response.data);
    this.isSupplementLabelLoaded = isSupplementLabel;
  }

  async updateRecipeRoundingRulesMetadata(selectedRule: RoundingRules) {
    this.recipeMetadata.roundingRuleIndex = selectedRule.id;
    this.dishesManagementState.loading = true;

    RecipesRepository.updateRecipeRoundingRule({
      ruleIndex: selectedRule.id,
      recipeId: this.dishesManagementState.dish.id,
      countryId: this.countryId
    })
      .then((response: any) => {
        if (response) {
          this.mapCombinedRecipeDetails(response.data);
        }
      })

      .finally(() => {
        this.dishesManagementState.loading = false;
      });
  }

  async getRecipeMetaData(recipeId: number) {
    const response = await RecipesRepository.getRecipeMetaData(recipeId);
    this.recipeMetadata = response.data;
    this.recipeMetadata.showAllergenOnPrintout = Boolean(response.data.showAllergenOnPrintout);
    const ruleIndex = response.data.roundingRuleIndex || 0;
    const selectedRule = this.nutritionRules.find((rule) => rule.id === ruleIndex);

    if (selectedRule) {
      this.selectedRoundingRule = selectedRule;
    }

    if(this.recipeMetadata.isSupplementLabel) {
      this.isSupplementLabelLoaded = true;
    }

    this.recipeMetadata.roundingRuleIndex = ruleIndex;
  }

  async updateShowAllergenOnPrintout(showAllergenOnPrintout: boolean) {
    const previousValue = this.recipeMetadata.showAllergenOnPrintout;
    this.recipeMetadata.showAllergenOnPrintout = showAllergenOnPrintout;

    if (!this.dishesManagementState.dish.id) {
      return;
    }

    try {
      await RecipesRepository.updateShowAllergenOnPrintout(
        this.dishesManagementState.dish.id,
        showAllergenOnPrintout
      );
    }
    catch {
      this.recipeMetadata.showAllergenOnPrintout = previousValue;
    }
  }

  removeRecipefromDish(recipeId: number) {
    let reference = this.dishesManagementState.dish.childRecipes.find(
      (r) => r.id === recipeId
    );

    if (reference) {
      this.dishesManagementState.dish.childRecipes.splice(
        this.dishesManagementState.dish.childRecipes.indexOf(reference),
        1
      );
    }

    if(this.dishesManagementState.dish.id){
      this.calculateNutritions();
    }

    this.setDirtyState();
  }

  order(type: string) {
    let orderType = parseInt(type);
    if (orderType === OrderType.Desc) {
      this.dishesManagementState.orders.orderType = OrderType.Desc;
    }
    else {
      this.dishesManagementState.orders.orderType = OrderType.Asc;
    }

    this.requestRecipes(
      new ListRequest({
        page: this.dishesManagementState.page,
        pageSize: this.dishesManagementState.pageSize,
        filters: this.dishesManagementState.filters,
        orders: [this.dishesManagementState.orders],
      })
    );
  }

  clear(obj: any) {
    if (obj === null) {
      this.resetSearchRecipes();
    }
  }

  requestRecipes(listRequest: ListRequest) {
    this.dishesManagementState.loading = true;

    RecipesRepository.listRecipes(listRequest)
      .then((response) => {
        this.dishesManagementState.recipes = response.data.result;
        this.dishesManagementState.recipes.map((recipe: any) => {
          recipe.nutritionAnalysis = mapNutritions(recipe.nutritionAnalysis);
        });
        this.dishesManagementState.count = response.data.count;
        this.dishesManagementState.totalCount = response.data.totalCount;
        this.dishesManagementState.loading = false;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(error.response.data.errorMessages[0].userVisibleMessage);
        }

        else {
          this.$snotify.error(this.translations.get("network-error"));
        }
      });
  }

  get disabledNext() {
    return (
      Math.ceil(
        this.dishesManagementState.totalCount /
          this.dishesManagementState.pageSize
      ) === this.dishesManagementState.page
    );
  }

  get showControls() {
    return (
      this.dishesManagementState.totalCount > this.dishesManagementState.count
    );
  }

  next() {
    if (!this.disabledNext) {
      this.dishesManagementState.page = this.dishesManagementState.page + 1;

      this.requestRecipes(
        new ListRequest({
          page: this.dishesManagementState.page,
          pageSize: this.dishesManagementState.pageSize,
          filters: this.dishesManagementState.filters,
          orders: [this.dishesManagementState.orders],
        })
      );
    }
  }

  prev() {
    if (this.dishesManagementState.page > 1) {
      this.dishesManagementState.page = this.dishesManagementState.page - 1;

      this.requestRecipes(
        new ListRequest({
          page: this.dishesManagementState.page,
          pageSize: this.dishesManagementState.pageSize,
          filters: this.dishesManagementState.filters,
          orders: [this.dishesManagementState.orders],
        })
      );
    }
  }

  deleteDish() {
    //@ts-ignore
    this.$snotify.async(
      this.translations.get("dish_toastr_deleting"),
      () =>
        new Promise((resolve, reject) => {
          DishesRepository.deleteDishes({
            dishIds: [this.dishesManagementState.dish.id],
          })
            .then(() => {
              resolve({
                body: this.translations.get("dish_toastr_deleted"),
              });
              this.$router.push({ name: "dishes" });
            })
            .catch((error) => {
              let message = "";
              if (error.response) {
                message =
                  error.response.data.errorMessages[0].userVisibleMessage;
              }
              else {
                message = this.translations.get("toastr_network_error");
              }
              reject({ body: message });
            });
        }),
      {
        timeout: 3000,
        closeOnClick: true,
      }
    );
    this.resetState();
  }

  submit(shouldRedirect: boolean = false) {
    this.$validator.validateAll().then((result) => {
      if (result) {
        const recipeCompositions: Partial<RecipeComposition>[] = this.dishesManagementState.dish.childRecipes.map(
          (subrecipe: Subrecipe) => {
            return {
              childRecipeId: subrecipe.recipe.id,
              parentRecipeId: this.dishesManagementState.dish.id,
              quantity: Number(subrecipe.quantity),
              recipeCompositionMeasurementId: subrecipe.recipeCompositionMeasurementId,
              isAddedSugar: subrecipe.isAddedSugar,
              foodGroupClassificationId: subrecipe.foodGroupClassificationId != null
                ? subrecipe.foodGroupClassificationId
                : 0,
            };
          }
        );

        this.$snotify.async(
          this.translations.get("dish_toastr_saving"),
          () => new Promise((resolve, reject) => {
            if (this.$route.name === "dishesEdit") {
              this.handleUpdateDish(resolve, reject, recipeCompositions, shouldRedirect);
            }

            else if (this.$route.name === "dishesAdd") {
              this.handleCreateDish(resolve, reject, recipeCompositions, shouldRedirect);
            }
          }),
          {
            timeout: 3000,
            closeOnClick: true,
          }
        );
        return;
      }
      this.$snotify.error(this.translations.get("validation-error-message"));
    });
  }

  handleUpdateDish(
    resolve: PromiseResolve<Snotify>,
    reject: PromiseReject,
    recipeCompositions: Partial<RecipeComposition>[],
    shouldRedirect: boolean
  ) {
    const productType = this.dishesManagementState.dish.recipeProductType
      ? this.dishesManagementState.dish.recipeProductType
      : createDefaultProductType();

    let updateRecipeDto: UpdateRecipe = {
      recipes: recipeCompositions.map((rc) => ({
        recipeId: rc.childRecipeId,
        quantity: this.normalizeQuantity(rc.quantity, 0),
        recipeCompositionMeasurementId: rc.recipeCompositionMeasurementId,
        isAddedSugar: rc.isAddedSugar,
        foodGroupClassificationId: rc.foodGroupClassificationId,
      })),
      ingredients: [],
      description: this.dishesManagementState.dish.description,
      recipeCategoryId: null,
      name: this.dishesManagementState.dish.name,
      recipeId: this.dishesManagementState.dish.id,
      instructions: this.dishesManagementState.dish.instructions,
      recipeServingType: RecipeServingType.Servings,
      servingCount: 1,
      servingQuantityInUnitOfMeasurement: null,
      servingUnitOfMeasurement: null,
      adjustedAddedSugarsValue: 0,
      nutritionCountry: this.countryId,
      referenceAmount: this.dishesManagementState.dish.referenceAmount,
      productTypeId: productType.id,
      nutrientTypesToCalculate: this.dishesManagementState.dish.nutritionCountry
        ? this.recipeMetadata.isSupplementLabel ? null : getPreviewNutrientsForCountry(
          this.dishesManagementState.dish.nutritionCountry.id)
        : null,
    };

    RecipesRepository.updateRecipe(updateRecipeDto)
      .then(async(response) => {
        await this.saveAllergenConfiguration(updateRecipeDto.recipeId);

        this.mapCombinedRecipeDetails(response.data.result);
        await this.hydrateDishStatusesFromSavedAnalysis(response.data.result.id);
        this.clearDirtyState();

        if (shouldRedirect) {
          this.$router.push({ name: "dishes" });
        }

        resolve({
          body: this.translations.get("dish_toastr_saved"),
        });
      })
      .catch((error) => {
        let message = "";
        if (error.response) {
          message =
            error.response.data.errorMessages[0].userVisibleMessage;
        }

        else {
          message = this.translations.get("toastr_network_error");
        }
        reject({ body: message });
      });
  }

  handleCreateDish(
    resolve: PromiseResolve<Snotify>,
    reject: PromiseReject,
    recipeCompositions: Partial<RecipeComposition>[],
    shouldRedirect: boolean
  ) {
    const productType = this.dishesManagementState.dish.recipeProductType
      ? this.dishesManagementState.dish.recipeProductType
      : createDefaultProductType();

    let createRecipeDto: CreateRecipe = {
      recipes: recipeCompositions.map((rc) => ({
        recipeId: rc.childRecipeId,
        quantity: this.normalizeQuantity(rc.quantity, 0),
        recipeCompositionMeasurementId: rc.recipeCompositionMeasurementId,
        isAddedSugar: rc.isAddedSugar,
        foodGroupClassificationId: rc.foodGroupClassificationId
      })),
      ingredients: [],
      description: this.dishesManagementState.dish.description,
      recipeCategoryId: null,
      name: this.dishesManagementState.dish.name,
      instructions: null,
      servingCount: 1,
      adjustedAddedSugarsValue: 0,
      isCombinedRecipe: true,
      nutritionCountry: this.countryId,
      referenceAmount: this.dishesManagementState.dish.referenceAmount,
      productTypeId: productType.id
    };

    RecipesRepository.createRecipe(createRecipeDto)
      .then(async(response) => {
        await this.saveAllergenConfiguration(response.data.id);

        this.$router.push({
          name: "dishesEdit",
          params: { id: response.data.id.toString() },
        });

        this.mapCombinedRecipeDetails(response.data);
        await this.hydrateDishStatusesFromSavedAnalysis(response.data.id);
        this.clearDirtyState();

        if (shouldRedirect === true) {
          this.$router.push({ name: "dishes" });
        }

        resolve({
          body: this.translations.get("dish_toastr_saved"),
        });
      })
      .catch((error: ErrorResponse) => {
        let message;
        if (error.response) {
          message = error.response.data.message;
        }

        else {
          message = "Unknown error when creating dish.";
        }

        reject({ body: message });
      });
  }

  calculateNutritions() {
    if (this.hasInvalidChildRecipeQuantity()) {
      return;
    }

    let recipeIds = this.dishesManagementState.dish.childRecipes.map((subrecipe: Subrecipe): RecipeCalculateSubrecipe => {
      return {
        recipeId: subrecipe.recipe.id,
        quantity: this.normalizeQuantity(subrecipe.quantity, 1),
        foodGroupClassificationId: subrecipe.foodGroupClassificationId != null
          ? subrecipe.foodGroupClassificationId
          : 0,
        isAddedSugar: Boolean(subrecipe.isAddedSugar),
        recipeCompositionMeasurementId: subrecipe.recipeCompositionMeasurementId,
      };
    });

    new Promise((resolve, reject) => {
      // NOTE: We always calculate both rounded and unrounded values, specifying the DV calculation method is unnecessary
      const calcRecipe: RecipeCalculate = {
        recipeId: this.dishesManagementState.dish.id,
        recipes: recipeIds,
        ingredients: [],
        servings: 1,
        lossAmount: 0,
        lossType: 0,
        cookingMethodId: -1,
        recipeServingType: RecipeServingType.Servings,
        servingCount: 1,
        caloriesCalculationMethod: this.calculationMethodModal.selectedCalculationMethod,
        referenceAmount: this.dishesManagementState.dish.referenceAmount,
        ruleIndex: this.recipeMetadata.roundingRuleIndex
      };

      DishesRepository.calculate(calcRecipe)
        .then((response) => {
          this.dishesManagementState.dish.nutritionAnalysis =
            mapNutritions(response.data.result);
          resolve({});
        })
        .catch((error) => {
          let message = "";
          if (error.response) {
            message = error.response.data.errorMessages[0].userVisibleMessage;
          }

          else {
            message = this.translations.get("toastr_network_error");
          }

          reject({ body: message });
        });
    });
  }

  resetSearchRecipes() {
    RecipesRepository.listRecipes(
      new ListRequest({
        page: this.dishesManagementState.page,
        pageSize: this.dishesManagementState.pageSize,
      })
    )
      .then((response) => {
        this.dishesManagementState.recipes = response.data.result;
        this.dishesManagementState.recipes.map((recipe: any) => {
          recipe.nutritionAnalysis = mapNutritions(recipe.nutritionAnalysis);
        });
        this.dishesManagementState.count = response.data.count;
        this.dishesManagementState.totalCount = response.data.totalCount;
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });
  }

  resetState() {
    // If trial upsell is open above Export Label, close only the upsell.
    if (this.showTrialUpsellModal) {
      this.closeTrialUpsellModal();
      return;
    }

    this.showDeleteDishModal = false;
    this.showExportLabelModal = false;
    // Refetch allergen data for the current dish/recipe when closing ExportLabel
    if (this.dishesManagementState && this.dishesManagementState.dish && this.dishesManagementState.dish.id) {
      this.loadAllergenConfiguration(this.dishesManagementState.dish.id);
    }
    this.closeExportLabelDemo();
  }

  openExportLabelDemo() {
    this.isExportLabelDemoLoading = true;
    this.showExportLabelDemoModal = true;
  }

  closeExportLabelDemo() {
    this.showExportLabelDemoModal = false;
    this.isExportLabelDemoLoading = false;
  }

  handleExportLabelDemoLoad() {
    this.isExportLabelDemoLoading = false;
  }

  async fetchEnabledClaims() {
    // If we don't have a country set, we shouldn't fire the request
    if (this.countryId != null) {
      const response = await ClaimsRepository.getEnabledClaimsForRecipe(
        this.dishesManagementState.dish.id,
        this.countryId
      );

      if (response && response.data) {
        this.enabledClaims = response.data;
      }
    }
  }

  getSubrecipeMeasurementByCompositionMeasurementId(compositionMeasurementId: number) {
    return this.subrecipeServingSizes.find(
      (rcm: RecipeCompositionMeasurement) => rcm.id === compositionMeasurementId
    )!.measurement;
  }

  fetchSubrecipeServingSizes() {
    RecipesRepository.getRecipeCompositionMeasurements().then((response) => {
      this.subrecipeServingSizes = response.data.result;

      this.subrecipeServingMeasurements = this.subrecipeServingSizes.map(
        (measurement: RecipeCompositionMeasurement) => measurement.measurement
      );
    });
  }

  updateReferenceAmount(){
    this.dishesManagementState.dish.referenceAmount = this.dishesManagementState.dish.referenceAmount.toString() != "" ? this.dishesManagementState.dish.referenceAmount : 0;

    this.setDirtyState();
  }

  selectSubRecipeMeasurement(
    measurement: { id: number; name: string },
    subRecipe: any
  ) {
    // Find the RecipeCompositionMeasurement object that corresponds to the selected measurement
    const measurementObj = this.subrecipeServingSizes.find(
      (rcm: RecipeCompositionMeasurement) => rcm.measurement.id === measurement.id
    );

    subRecipe.recipeCompositionMeasurementId = measurementObj.id;
    this.calculateNutritions();
  }

  async fetchNutritionCountries(){
    const response = await NutritionLabelRepository.listNutritionCountries();
    this.nutritionCountries = response.data.result;
  }

  get translations() {
    return this.localizationState.translations;
  }

  get allergenStatusLegendTooltipContent() {
    return [
      "<div class='allergens-popover-legend'>",
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge ai'><i class='fa fa-check'></i></span><span>${this.translations.get("allergens_ai_legend_ai") || "AI detected this allergen."}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge removed'><i class='fa fa-times'></i></span><span>${this.translations.get("allergens_ai_legend_removed") || "Manually marked as not an allergen."}</span></div>`,
      `<div class='allergens-popover-legend-row'><span class='allergens-popover-badge user'><i class='fa fa-check'></i><span class='allergens-popover-badge-accent'><i class='fa fa-question'></i></span></span><span>${this.translations.get("allergens_ai_legend_user_added") || "Manually added as an allergen."}</span></div>`,
      "</div>"
    ].join("");
  }

  get showAllergenAiButton() {
    return this.accountState.userDetails.isTrial || this.isAllergenAiAddonActive;
  }

  get isAllergenAiAddonActive() {
    if (this.accountState.userDetails.isTrial) {
      return true;
    }

    const addOns = this.accountState?.userDetails?.addOns || this.$store.getters.getUserDetails?.addOns || [];
    return addOns.some((addOn: any) => {
      const addOnType = typeof addOn === "object" ? addOn.addOn : addOn;
      return addOn?.isActive !== false && (addOnType === AddOnType.AllergenAI || addOnType === "AllergenAI");
    });
  }

  getAllergenAiUpsellMessage() {
    const translation = this.translations.get("upsell_allergen_ai_message");
    return translation === "---"
      ? "'AI Allergen Detector' is available as a paid add-on. Upgrade your subscription to access the module."
      : translation;
  }

  get shouldShowDishAiDetector() {
    const dish = this.dishesManagementState.dish;
    return Boolean(dish.id && !dish.hasAllergenAnalysis);
  }

  getAllergenStatusVariant(recipe: Recipe) {
    return getAllergenStatusMeta(recipe).variant;
  }

  getAllergenStatusTooltip(recipe: Recipe) {
    return getAllergenStatusMeta(recipe).title;
  }

  goToCurrentDishAllergenAi() {
    const dish = this.dishesManagementState.dish;

    if (this.isDirty || !dish.id) {
      return;
    }

    this.$router.push({
      name: "allergensAi",
      query: {
        tab: "combinedRecipes",
        id: String(dish.id),
        name: dish.name,
        analyzed: dish.hasAllergenAnalysis ? "1" : "0",
      },
    });
  }

  goToRecipeAllergenAi(subrecipe: Subrecipe) {
    this.$router.push({
      name: "allergensAi",
      query: {
        tab: "recipes",
        id: String(subrecipe.recipe.id),
        name: subrecipe.recipe.name,
        analyzed: subrecipe.recipe.hasAllergenAnalysis ? "1" : "0",
      },
    });
  }

  mounted() {
    Vue.nextTick(() => {
      (<any>window).$.Pages.init();
      this.initSearch();
    });
  }

  async getDishDetails() {
    const response = await RecipesRepository.getRecipeDetails(parseInt(this.$route.params.id), true);

    if (!response.data) {
      this.isDirty = true;
      await this.$router.push({ name: "dishesAdd" });
    }

    try {
      await this.mapCombinedRecipeDetails(response.data);
      await this.loadAllergenConfiguration(response.data.id);
      await this.hydrateDishStatusesFromSavedAnalysis(response.data.id);
      this.clearDirtyState();
    }

    catch {
      this.isDirty = true;
      await this.$router.push({ name: "dishesAdd" });
    }
  }

  async mapCombinedRecipeDetails(recipeResponse: Dish) {
    const existingChildRecipeStatusById = new Map<number, { hasAllergenAnalysis: boolean; hasAllergen: boolean }>();

    for (const childRecipe of this.dishesManagementState.dish.childRecipes || []) {
      const childRecipeId = childRecipe?.recipe?.id;

      if (!childRecipeId) {
        continue;
      }

      existingChildRecipeStatusById.set(childRecipeId, {
        hasAllergenAnalysis: Boolean(childRecipe.recipe.hasAllergenAnalysis),
        hasAllergen: Boolean(childRecipe.recipe.hasAllergen),
      });
    }

    this.dishesManagementState.dish = {
      ...this.dishesManagementState.dish,
      ...recipeResponse
    };

    for (const childRecipe of this.dishesManagementState.dish.childRecipes || []) {
      const childRecipeId = childRecipe?.recipe?.id;

      if (!childRecipeId || !existingChildRecipeStatusById.has(childRecipeId)) {
        continue;
      }

      const existingStatus = existingChildRecipeStatusById.get(childRecipeId)!;
      childRecipe.recipe.hasAllergenAnalysis = existingStatus.hasAllergenAnalysis;
      childRecipe.recipe.hasAllergen = existingStatus.hasAllergen;
    }

    this.dishesManagementState.dish.servings = 1;
    this.dishesManagementState.dish.instructions = null;
    this.dishesManagementState.dish.recipeCategory = null;
    this.dishesManagementState.dish.nutritionAnalysis = mapNutritions(
      this.dishesManagementState.dish.nutritionAnalysis
    );
    this.dishesManagementState.dish.referenceAmount = recipeResponse.referenceAmount;

    await this.fillNutritionCountry(recipeResponse.nutritionCountry);
    await this.getRecipeMetaData(recipeResponse.id);

    if(this.foodProductTypes.length > 0) {
      this.dishesManagementState.dish.recipeProductType = this.foodProductTypes.find(
        (x) => x.id === recipeResponse.productTypeId
      ) || createDefaultProductType();
    }

    this.calculationMethodModal.selectedCalculationMethod = recipeResponse.caloriesCalculationMethod;
    this.calculationMethodModal.selectedDailyValueCalculation = recipeResponse.dailyValueCalculationMethod;
    this.calculationMethodModal.selectedProteinDailyValueCalculation = recipeResponse.proteinDvCalculationMethod;
    this.calculationMethodModal.pdcaasScore = recipeResponse.pdcaasScore;

    await this.fetchEnabledClaims();

    this.dishesManagementState.loading = false;
  }

  private async fillNutritionCountry(nutritionCountryId: any) {
    const nutritionCountry = this.nutritionCountries.find((x) => x.id === nutritionCountryId);
    if (nutritionCountry) {
      this.dishesManagementState.dish.nutritionCountry = nutritionCountry;
      await this.fetchPackageType(nutritionCountry.id);
    }
  }

  async created() {
    await this.fetchNutritionCountries();

    this.$store.dispatch(
      TRANSLATIONS_FETCH,
      {
        category: "validationrules",
        collections: ["recipes"],
      }
    );

    this.$store
      .dispatch(TRANSLATIONS_FETCH, {
        category: "pages",
        collections: [
          "common",
          "recipes",
          "dishes",
          "nutrition-labels",
          "cautionary"
        ],
      })
      .then(() => {
        if (this.$route.name === "dishesEdit") {
          document.title = this.translations.get("dish_title_edit") + " " + this.translations.get("meta_title_postfix");
        }

        else {
          document.title = this.translations.get("dish_title_add") + " " + this.translations.get("meta_title_postfix");
          this.fetchPackageType();
        }
      })
      .catch((error) => {
        if (error.response) {
          this.$snotify.error(
            error.response.data.errorMessages[0].userVisibleMessage
          );
        }
        else this.$snotify.error(this.translations.get("network-error"));
      });

    this.fetchSubrecipeServingSizes();
    if (this.$route.name === "dishesEdit") {
      this.dishesManagementState.loading = true;
      await this.getDishDetails();
    }

    this.resetSearchRecipes();
  }
}
export default DishesManagement;
